<template>
  <div>
    <div id="k_chart2"></div>
    <div class="k_time">
      <span>{{ t1 }}</span>
      <span v-if="showT2">{{ t2 }}</span>   <!-- 只有数据≥60条才显示 -->
    </div>
    <div id="k_chart3"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  props: {
    stockData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      t1: '',
      t2: '',
      showT2: false,        // 控制是否显示结束日期
      chart1: null,
      chart2: null,
    }
  },
  mounted() {
    this.drawChart()
  },
  watch: {
    stockData: {
      handler() {
        this.drawChart()
      },
      deep: true
    }
  },
  methods: {
    fontSize(res) {
      const docEl = document.documentElement
      const clientWidth = window.innerWidth || docEl.clientWidth || document.body.clientWidth
      if (!clientWidth) return res
      const fontSize = clientWidth / 750
      return res * fontSize
    },
    drawChart() {
      const data = this.stockData
      // 1. 获取并排序日期
      let dates = Object.keys(data).sort()
      if (dates.length === 0) return

      const len = dates.length
      // 2. 设置 t1 和 t2（t2 只在数据≥60条时显示）
      this.t1 = dates[0]
      if (len >= 60) {
        this.t2 = dates[len - 1]
        this.showT2 = true
      } else {
        this.t2 = ''
        this.showT2 = false
      }

      // 3. 补全日期至 60 个（参考 drawK 逻辑）
      const paddedDates = [...dates]
      if (len < 60) {
        for (let i = 0; i < 60 - len; i++) {
          paddedDates.push('')   // 空字符串占位
        }
      }

      // 4. 根据补全后的日期提取数据，空日期对应的数据为 null
      const categoryData = paddedDates
      const values = paddedDates.map(date => {
        if (date === '') return [null, null, null, null]   // K线空数据
        const d = data[date]
        return [d.open, d.close, d.low, d.high]
      })
      const ma5 = paddedDates.map(date => date === '' ? null : data[date].ma_a)
      const ma10 = paddedDates.map(date => date === '' ? null : data[date].ma_b)
      const ma20 = paddedDates.map(date => date === '' ? null : data[date].ma_c)

      // 成交量（同样补全）
      const volumes = paddedDates.map(date => {
        if (date === '') return { value: 0, isUp: true }
        const d = data[date]
        return {
          value: parseFloat(d.volume) || 0,
          isUp: d.close >= d.open
        }
      })

      // 颜色定义（保持不变）
      const upColor = '#FE484A'
      const upBorderColor = '#FE484A'
      const downColor = '#0AC890'
      const downBorderColor = '#0AC890'
      const ma5Color = '#63A0FF'
      const ma10Color = '#FFA500'
      const ma20Color = '#9ACD32'

      // 计算 Y 轴最大最小值（只取有效数据）
      const allPrices = []
      values.forEach(item => {
        if (item[0] !== null) { // 只取非空数据
          allPrices.push(item[1], item[2], item[3])
        }
      })
      const yMax = allPrices.length ? Math.max(...allPrices) : 100
      const yMin = allPrices.length ? Math.min(...allPrices) : 0

      // ========== K线图配置 ==========
      const option1 = {
        animation: false,
        grid: {
          left: 10,
          right: 10,
          top: 25,
          bottom: 35,
        },
        xAxis: {
          type: 'category',
          data: categoryData,
          scale: true,
          boundaryGap: false,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          splitNumber: 20,
          min: 'dataMin',
          max: 'dataMax',
          axisLabel: { show: false }
        },
        yAxis: {
          min: yMin,
          max: yMax,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: { color: '#E8E8E8', type: 'dashed' }
          },
          position: 'left',
          axisLabel: {
            color: '#939393',
            fontSize: this.fontSize(18),
            inside: true,
            formatter: function(value) {
              if (value === yMax || value === yMin) {
                return value.toFixed(2)
              }
              return ''
            }
          }
        },
        series: [
          {
            name: 'K线图',
            type: 'candlestick',
            data: values,
            itemStyle: {
              color: upColor,
              color0: downColor,
              borderColor: upBorderColor,
              borderColor0: downBorderColor
            }
          },
          {
            name: 'MA5',
            type: 'line',
            data: ma5,
            smooth: true,
            lineStyle: { opacity: 1, color: ma5Color },
            symbol: 'circle',
            showSymbol: true,
            color: ma5Color,
            symbolSize: 0,
            connectNulls: false,   // 空值处断开连线
            itemStyle: {
              normal: {},
              emphasis: {
                borderColor: '#fff',
                borderWidth: 2,
                shadowColor: ma5Color,
                shadowBlur: 10
              }
            }
          },
          {
            name: 'MA10',
            type: 'line',
            data: ma10,
            smooth: true,
            lineStyle: { opacity: 1, color: ma10Color },
            symbol: 'circle',
            showSymbol: true,
            color: ma10Color,
            symbolSize: 0,
            connectNulls: false,
            itemStyle: {
              normal: {},
              emphasis: {
                borderColor: '#fff',
                borderWidth: 2,
                shadowColor: ma10Color,
                shadowBlur: 10
              }
            }
          },
          {
            name: 'MA20',
            type: 'line',
            data: ma20,
            smooth: true,
            lineStyle: { opacity: 1, color: ma20Color },
            symbol: 'circle',
            showSymbol: true,
            color: ma20Color,
            symbolSize: 0,
            connectNulls: false,
            itemStyle: {
              normal: {},
              emphasis: {
                borderColor: '#fff',
                borderWidth: 2,
                shadowColor: ma20Color,
                shadowBlur: 10
              }
            }
          }
        ]
      }

      // ========== 成交量图配置 ==========
      const option2 = {
        animation: false,
        grid: {
          left: 5,
          right: 5,
          top: 5,
          bottom: 5
        },
        xAxis: {
          type: 'category',
          data: categoryData,
          scale: true,
          boundaryGap: false,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#939393',
            fontSize: this.fontSize(18),
            show: false,
            interval: Math.ceil(categoryData.length / 8)
          }
        },
        yAxis: {
          scale: true,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          position: 'left',
          axisLabel: {
            color: '#939393',
            fontSize: this.fontSize(18),
            formatter: function(value) {
              if (value >= 10000) {
                return (value / 10000).toFixed(1) + '万'
              }
              return value
            }
          }
        },
        series: [
          {
            name: '成交量',
            type: 'bar',
            data: volumes.map(item => item.value),
            itemStyle: {
              color: function(params) {
                return volumes[params.dataIndex].isUp ? upColor : downColor
              }
            },
            barWidth: '60%',
            barGap: '10%'
          }
        ]
      }

      // ========== 渲染图表 ==========
      if (this.chart1) {
        this.chart1.clear()
        this.chart2.clear()
      }

      this.chart1 = echarts.init(document.getElementById('k_chart2'))
      this.chart1.setOption(option1)

      this.chart2 = echarts.init(document.getElementById('k_chart3'))
      this.chart2.setOption(option2)

      const resizeHandler = () => {
        this.chart1.resize()
        this.chart2.resize()
      }
      window.addEventListener('resize', resizeHandler)
    }
  }
}
</script>

<style lang="scss">
.k_time {
    font-size: 0.24rem;
    color: #939393;
    position: relative;
    height:16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
</style>